import React from 'react';
import classNames from 'classnames';

import { CoreLink, DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import type { ParagraphQuote as Type } from '../../../types/uncinc_profile/paragraphs';
import type { MediaReferenceField } from '../../../types/uncinc_profile/helpers/media';

import './style.scss';

interface ParagraphQuoteProps {
  id?: string;
  entity: ImmutableEntity<Type>;
  noBootstrap?: boolean;
}

const ParagraphQuote: React.FC<ParagraphQuoteProps> = ({
  id, entity, noBootstrap,
}) => {
  const paragraphQuote = entity.toJS();
  const quote = paragraphQuote.field_body[0].processed;

  let authorName: string | undefined;
  let authorFunction: string | undefined;
  let authorLink: string | undefined;
  let authorProfilePicture: MediaReferenceField<'image'> | undefined;

  const authorField = paragraphQuote.field_author[0]?.entity;

  if (authorField?.field_first_name[0].value) {
    // If author is linked from a Profile
    authorName = [
      authorField.field_first_name[0].value,
      authorField.field_last_name[0].value,
    ].join(' ');
    authorFunction = authorField.field_functions[0]?.entity.name[0].value;
    [authorProfilePicture] = authorField.field_media_item;
    authorLink = paragraphQuote.field_author[0]?.url;
  } else {
    // otherwise, use hardcoded author field
    authorName = paragraphQuote.field_name[0]?.value;
    authorFunction = paragraphQuote.field_label[0]?.value;
    authorProfilePicture = paragraphQuote.field_profile_picture?.[0];
  }

  const containerClasses = classNames(
    'Paragraph',
    'ParagraphQuote',
  );

  return (
    <div id={id} className={containerClasses}>
      <Container>
        <div className={noBootstrap ? 'noBootstrapRow' : 'row'}>
          <div className={noBootstrap ? 'noBootstrapColumn' : 'col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2'}>
            <figure className="ParagraphQuote__wrapper">
              <blockquote className="ParagraphQuote__quote-wrapper">
                <WysiwygField
                  className="ParagraphQuote__quote quote"
                  property="schema:text"
                  html={quote}
                />
              </blockquote>
              {authorName && (
                <figcaption className="ParagraphQuote__caption">
                  {authorProfilePicture && (
                    <DynamicMediaItem
                      className="ParagraphQuote__profile-picture"
                      media={authorProfilePicture}
                      imageAspectRatio="1_1"
                      alt={authorProfilePicture.alt || authorName}
                    />
                  )}
                  <div className="ParagraphQuote__author-info">
                    {authorLink ? (
                      <CoreLink
                        href={authorLink}
                        className="ParagraphQuote__author-link"
                      >
                        {authorName}
                      </CoreLink>
                    ) : (
                      <strong className="ParagraphQuote__author-link">
                        {authorName}
                      </strong>
                    )}
                    {authorFunction && (
                      <>
                        {', '}
                        <span className="ParagraphQuote__author-function">
                          {authorFunction}
                        </span>
                      </>
                    )}
                  </div>
                </figcaption>
              )}
            </figure>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParagraphQuote;
